import React, { useCallback, useRef } from 'react';
import { Button, Card, Col, Row, Space } from 'antd';
import TableContextProvider, { useTableControls } from 'common/components/table/table-context';
import TableBase from 'common/components/table/table-base';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { SearchFilter } from 'common/components/table/filters/search-filter/search-filter';
import { ColumnFilter } from 'common/components/table/filters/column-filter/column-filter';
import { IFilterConfig } from 'common/components/table/filters/column-filter/column-filter.types';
import { ColumnVisibility } from 'common/components/table/filters/column-visibility/column-visibility';
import activityLogsService from 'features/eiq-manage/service/activity-logs-service/activity-logs.service';
import { activityLogsTableInitialColumns } from './activity-logs.config';
import { actionOptions, entityTypeOptions } from 'features/eiq-manage/enums/activity-logs.enum';
import { debounce } from 'lodash';
import { downloadCsv } from 'common/helpers/downloadCsv.helper';
import exportService from 'common/services/export-service/export.service';

const EXPORT_PAGE_LIMIT = 10_000;

const ActivityLogs = () => {
  const debouncedFetchRef = useRef<((...args: any[]) => void) | null>(null);

  if (!debouncedFetchRef.current) {
    debouncedFetchRef.current = debounce(
      (resolve: (value: any) => void, pageLimit: any, filter: any, sort: any, search: any, isArchive: any) => {
        activityLogsService.getAll(pageLimit, filter, sort, search).then(resolve);
      },
      500,
    );
  }

  const debounceDataFetch = useCallback((pageLimit: any, filter: any, sort: any, search: any) => {
    return new Promise((resolve) => {
      debouncedFetchRef.current?.(resolve, pageLimit, filter, sort, search);
    });
  }, []);

  const tableControls = useTableControls({
    onFetch: (pageLimit: any, filter: any, sort: any, search: any) => debounceDataFetch(pageLimit, filter, sort, search),
    initialColumns: activityLogsTableInitialColumns,
  });

  const getTableQuery = () => ({
    pageLimit: { page: 1, limit: EXPORT_PAGE_LIMIT },
    tableFilters: tableControls.getFilters(),
    tableSortBy: tableControls.getSortBy(),
  });

  const handleExportCsv = async () => {
    const { pageLimit, tableFilters, tableSortBy } = getTableQuery();
    const blob = await exportService.processActivityLogs(pageLimit, tableFilters, tableSortBy);
    downloadCsv(blob, 'audit-logs.csv');
  };

  return (
    <Card className="eiq-card eiq-card-border eiq-configuration">
      <Row className="eiq-card-header">
        <Col span={24}>
          <h1 className="h1">Audit logs</h1>
        </Col>
      </Row>
      <TableContextProvider controls={tableControls}>
        <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
          <Col flex="auto">
            <SearchFilter
              adaptiveSearch={true}
              enableProgressiveSearch={true}
              placeholder="Search by entity type, user type, action type, user name, values"
              rulesForColumn={{}}
              rightSide={
                <Space size={8}>
                  <Button type="primary" size="middle" onClick={handleExportCsv} className="mobile-btn">
                    Export CSV
                  </Button>
                  <ColumnVisibility isColumnReorderAllow={true} />
                  <ColumnFilter filterConfig={incidentReportListFilter} />
                </Space>
              }
            />
          </Col>
        </Row>
        <TableBase selectFirstRowByDefault={false} />
      </TableContextProvider>
    </Card>
  );
};

export default ActivityLogs;

export const incidentReportListFilter: IFilterConfig[] = [
  {
    name: 'entityType',
    filterType: FilterTypesEnum.Equals,
    label: 'Entity Type',
    type: {
      options: entityTypeOptions,
      type: 'SELECT',
    },
  },
  {
    name: 'action',
    filterType: FilterTypesEnum.Equals,
    label: 'Action',
    type: {
      options: actionOptions,
      type: 'SELECT',
    },
  },
  {
    name: 'occurredAt',
    filterType: FilterTypesEnum.Equals,
    label: 'Dates',
    type: {
      nameEnd: 'occurredAt',
      type: 'DATE_RANGE',
    },
    onChange(filterValue, tableContext) {
      if (!filterValue) {
        return;
      }

      tableContext.updateFilter(
        'occurredAt',
        [
          { operator: FilterTypesEnum.GreaterThanOrEqual, value: filterValue.start, innerFilterType: 'AND' },
          { operator: FilterTypesEnum.LessThan, value: filterValue.end },
        ],
        'AND',
      );
    },
  },
];
